import React, { useState, useEffect, useRef } from 'react';
import { apiClient } from '../../App';
import { Layout } from '../../components/Layout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import { 
  MessageCircle, Pin, PinOff, Trash2, Search, 
  ChevronDown, ChevronUp, Clock, Users, RefreshCw, Send, Image, X, Loader2, Link, Plus
} from 'lucide-react';

const AdminCommunity = () => {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedNote, setExpandedNote] = useState(null);
  const [noteComments, setNoteComments] = useState({});
  const [pinningNote, setPinningNote] = useState(null);
  const [deletingNote, setDeletingNote] = useState(null);
  const [stats, setStats] = useState({ totalNotes: 0, totalComments: 0, pinnedCount: 0 });
  
  // New state for admin interactions
  const [newNote, setNewNote] = useState('');
  const [posting, setPosting] = useState(false);
  const [commentInputs, setCommentInputs] = useState({});
  const [postingComment, setPostingComment] = useState({});
  
  // Image upload state
  const [imageUrl, setImageUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const imageInputRef = useRef(null);
  
  // Link state
  const [links, setLinks] = useState([]);
  const [showLinkForm, setShowLinkForm] = useState(false);
  const [newLinkUrl, setNewLinkUrl] = useState('');
  const [newLinkLabel, setNewLinkLabel] = useState('');
  
  // Reaction tooltip state
  const [reactionNames, setReactionNames] = useState({});
  const [loadingReactions, setLoadingReactions] = useState({});

  useEffect(() => {
    fetchNotes();
  }, []);

  const fetchNotes = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get('/community-notes?limit=100');
      setNotes(res.data);
      
      // Calculate stats
      const pinnedCount = res.data.filter(n => n.is_pinned).length;
      const totalComments = res.data.reduce((sum, n) => sum + (n.comments_count || 0), 0);
      setStats({
        totalNotes: res.data.length,
        totalComments,
        pinnedCount
      });
    } catch (err) {
      console.error('Failed to fetch community notes:', err);
      toast.error('Failed to load community notes');
    } finally {
      setLoading(false);
    }
  };

  const togglePinNote = async (noteId, currentlyPinned) => {
    setPinningNote(noteId);
    try {
      const res = await apiClient.post(`/admin/community-notes/${noteId}/pin`);
      setNotes(notes.map(note => {
        if (note.id === noteId) {
          return { ...note, is_pinned: res.data.is_pinned };
        }
        return note;
      }));
      toast.success(res.data.is_pinned ? 'Note pinned' : 'Note unpinned');
      // Update stats
      setStats(prev => ({
        ...prev,
        pinnedCount: res.data.is_pinned ? prev.pinnedCount + 1 : prev.pinnedCount - 1
      }));
    } catch (err) {
      toast.error('Failed to update pin status');
    } finally {
      setPinningNote(null);
    }
  };

  const deleteNote = async (noteId) => {
    if (!window.confirm('Are you sure you want to delete this note and all its comments?')) return;
    
    setDeletingNote(noteId);
    try {
      await apiClient.delete(`/community-notes/${noteId}`);
      const deletedNote = notes.find(n => n.id === noteId);
      setNotes(notes.filter(n => n.id !== noteId));
      toast.success('Note deleted');
      // Update stats
      setStats(prev => ({
        totalNotes: prev.totalNotes - 1,
        totalComments: prev.totalComments - (deletedNote?.comments_count || 0),
        pinnedCount: deletedNote?.is_pinned ? prev.pinnedCount - 1 : prev.pinnedCount
      }));
    } catch (err) {
      toast.error('Failed to delete note');
    } finally {
      setDeletingNote(null);
    }
  };

  const fetchComments = async (noteId) => {
    try {
      const res = await apiClient.get(`/community-notes/${noteId}/comments`);
      setNoteComments(prev => ({ ...prev, [noteId]: res.data }));
    } catch (err) {
      console.error('Failed to fetch comments:', err);
    }
  };

  const deleteComment = async (noteId, commentId) => {
    try {
      await apiClient.delete(`/community-notes/comments/${commentId}`);
      setNoteComments(prev => ({
        ...prev,
        [noteId]: prev[noteId].filter(c => c.id !== commentId)
      }));
      // Update note's comment count
      setNotes(notes.map(note => {
        if (note.id === noteId) {
          return { ...note, comments_count: Math.max(0, (note.comments_count || 0) - 1) };
        }
        return note;
      }));
      setStats(prev => ({ ...prev, totalComments: prev.totalComments - 1 }));
      toast.success('Comment deleted');
    } catch (err) {
      toast.error('Failed to delete comment');
    }
  };

  // Create a new note as admin
  const createNote = async () => {
    if (!newNote.trim() && !imageUrl) {
      toast.error('Please write something or add an image to share!');
      return;
    }
    setPosting(true);
    try {
      const res = await apiClient.post('/community-notes', { 
        content: newNote,
        image_url: imageUrl || null,
        links: links.length > 0 ? links : null
      });
      setNotes([res.data, ...notes]);
      setNewNote('');
      setImageUrl('');
      setLinks([]);
      setShowLinkForm(false);
      setStats(prev => ({ ...prev, totalNotes: prev.totalNotes + 1 }));
      toast.success('Note posted!');
    } catch (err) {
      toast.error('Failed to post note');
    } finally {
      setPosting(false);
    }
  };

  // Add link helpers
  const addLink = () => {
    if (!newLinkUrl.trim() || !newLinkLabel.trim()) {
      toast.error('Please enter both URL and label');
      return;
    }
    let url = newLinkUrl.trim();
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      url = 'https://' + url;
    }
    setLinks([...links, { url, label: newLinkLabel.trim() }]);
    setNewLinkUrl('');
    setNewLinkLabel('');
    setShowLinkForm(false);
  };

  const removeLink = (index) => {
    setLinks(links.filter((_, i) => i !== index));
  };

  // Handle image upload
  const handleImageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload a JPEG, PNG, GIF, or WebP image');
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const res = await apiClient.post('/upload/community-image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
      const fullUrl = `${baseUrl}${res.data.url}`;
      setImageUrl(fullUrl);
      toast.success('Image uploaded!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload image');
    } finally {
      setUploading(false);
      if (imageInputRef.current) {
        imageInputRef.current.value = '';
      }
    }
  };

  const fetchReactionNames = async (noteId) => {
    if (reactionNames[noteId] || loadingReactions[noteId]) return;
    
    setLoadingReactions(prev => ({ ...prev, [noteId]: true }));
    try {
      const res = await apiClient.get(`/community-notes/${noteId}/reactions`);
      const names = res.data.map(r => r.user_name || 'Unknown');
      setReactionNames(prev => ({ ...prev, [noteId]: names }));
    } catch (err) {
      console.error('Failed to fetch reactions:', err);
    } finally {
      setLoadingReactions(prev => ({ ...prev, [noteId]: false }));
    }
  };

  // Toggle fist bump reaction
  const toggleReaction = async (noteId) => {
    try {
      // Backend uses a single toggle endpoint
      await apiClient.post(`/community-notes/${noteId}/react`);
      const note = notes.find(n => n.id === noteId);
      if (note?.user_reacted) {
        setNotes(notes.map(n => n.id === noteId ? { 
          ...n, 
          user_reacted: false, 
          reactions_count: Math.max(0, (n.reactions_count || 0) - 1) 
        } : n));
      } else {
        setNotes(notes.map(n => n.id === noteId ? { 
          ...n, 
          user_reacted: true, 
          reactions_count: (n.reactions_count || 0) + 1 
        } : n));
      }
    } catch (err) {
      toast.error('Failed to update reaction');
    }
  };

  // Add a comment to a note
  const addComment = async (noteId) => {
    const content = commentInputs[noteId]?.trim();
    if (!content) return;
    
    setPostingComment(prev => ({ ...prev, [noteId]: true }));
    try {
      const res = await apiClient.post(`/community-notes/${noteId}/comments`, { content });
      setNoteComments(prev => ({
        ...prev,
        [noteId]: [...(prev[noteId] || []), res.data]
      }));
      setNotes(notes.map(n => n.id === noteId ? { 
        ...n, 
        comments_count: (n.comments_count || 0) + 1 
      } : n));
      setCommentInputs(prev => ({ ...prev, [noteId]: '' }));
      setStats(prev => ({ ...prev, totalComments: prev.totalComments + 1 }));
      toast.success('Comment added!');
    } catch (err) {
      toast.error('Failed to add comment');
    } finally {
      setPostingComment(prev => ({ ...prev, [noteId]: false }));
    }
  };

  const toggleExpand = async (noteId) => {
    if (expandedNote === noteId) {
      setExpandedNote(null);
    } else {
      setExpandedNote(noteId);
      if (!noteComments[noteId]) {
        await fetchComments(noteId);
      }
    }
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Filter notes by search term
  const filteredNotes = notes.filter(note => 
    note.content.toLowerCase().includes(searchTerm.toLowerCase()) ||
    note.user_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Sort: pinned first, then by date
  const sortedNotes = [...filteredNotes].sort((a, b) => {
    if (a.is_pinned && !b.is_pinned) return -1;
    if (!a.is_pinned && b.is_pinned) return 1;
    return new Date(b.created_at) - new Date(a.created_at);
  });

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-[calc(100vh-64px)]">
          <div className="w-8 h-8 border-2 border-[#EFB342] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-4 md:p-8 max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-stone-900 flex items-center gap-3">
              <MessageCircle className="text-[#EFB342]" />
              Community Notes
            </h1>
            <p className="text-stone-600 text-sm mt-1">Manage and moderate community content</p>
          </div>
          <Button
            onClick={fetchNotes}
            variant="outline"
            className="border-stone-300 text-stone-700 hover:bg-stone-100"
          >
            <RefreshCw size={16} className="mr-2" />
            Refresh
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="bg-white border border-stone-200 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#EFB342]/20 rounded-lg flex items-center justify-center">
                <MessageCircle className="text-[#EFB342]" size={20} />
              </div>
              <div>
                <p className="text-2xl font-bold text-stone-900">{stats.totalNotes}</p>
                <p className="text-stone-500 text-xs">Total Notes</p>
              </div>
            </div>
          </div>
          <div className="bg-white border border-stone-200 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                <Users className="text-blue-400" size={20} />
              </div>
              <div>
                <p className="text-2xl font-bold text-stone-900">{stats.totalComments}</p>
                <p className="text-stone-500 text-xs">Total Comments</p>
              </div>
            </div>
          </div>
          <div className="bg-white border border-stone-200 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-orange-500/20 rounded-lg flex items-center justify-center">
                <Pin className="text-orange-400" size={20} />
              </div>
              <div>
                <p className="text-2xl font-bold text-stone-900">{stats.pinnedCount}</p>
                <p className="text-stone-500 text-xs">Pinned Notes</p>
              </div>
            </div>
          </div>
        </div>

        {/* Search */}
        <div className="relative mb-6">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-500" size={18} />
          <Input
            type="text"
            placeholder="Search by content or user name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 bg-white border-stone-300 text-stone-900 placeholder:text-stone-500"
          />
        </div>

        {/* Admin Post Note */}
        <div className="bg-gradient-to-r from-[#EFB342]/10 to-orange-500/10 border border-[#EFB342]/30 rounded-xl p-4 mb-6">
          <div className="flex items-center gap-2 mb-3">
            <MessageCircle className="text-[#EFB342]" size={18} />
            <span className="text-stone-900 font-medium">Post as Admin</span>
          </div>
          <Textarea
            value={newNote}
            onChange={(e) => setNewNote(e.target.value)}
            placeholder="Share a message with the community..."
            className="bg-stone-50 border-stone-300 text-stone-900 placeholder:text-stone-500 mb-3"
            rows={3}
          />
          
          {/* Image Preview */}
          {imageUrl && (
            <div className="relative mb-3 inline-block">
              <img 
                src={imageUrl} 
                alt="Upload preview" 
                className="max-h-32 rounded-lg border border-stone-300"
              />
              <button
                onClick={() => setImageUrl('')}
                className="absolute -top-2 -right-2 bg-red-500 text-stone-900 rounded-full p-1 hover:bg-red-600"
              >
                <X size={14} />
              </button>
            </div>
          )}
          
          {/* Links Preview */}
          {links.length > 0 && (
            <div className="mb-3 space-y-2">
              {links.map((link, index) => (
                <div key={index} className="flex items-center gap-2 bg-stone-100 rounded-lg px-3 py-2 border border-stone-300">
                  <Link size={14} className="text-[#EFB342]" />
                  <span className="text-stone-700 text-sm flex-1 truncate">{link.label}</span>
                  <span className="text-stone-500 text-xs truncate max-w-[150px]">{link.url}</span>
                  <button
                    onClick={() => removeLink(index)}
                    className="text-red-400 hover:text-red-300 p-1"
                  >
                    <X size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}
          
          {/* Add Link Form */}
          {showLinkForm && (
            <div className="mb-3 p-3 bg-stone-100 rounded-lg border border-stone-300 space-y-2">
              <Input
                value={newLinkLabel}
                onChange={(e) => setNewLinkLabel(e.target.value)}
                placeholder="Link label (e.g., 'Check out this recipe')"
                className="bg-white border-stone-300 text-stone-900 text-sm"
              />
              <Input
                value={newLinkUrl}
                onChange={(e) => setNewLinkUrl(e.target.value)}
                placeholder="URL (e.g., https://example.com)"
                className="bg-white border-stone-300 text-stone-900 text-sm"
              />
              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={addLink}
                  className="bg-[#EFB342] text-black hover:bg-[#D99F30]"
                >
                  <Plus size={14} className="mr-1" /> Add Link
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => { setShowLinkForm(false); setNewLinkUrl(''); setNewLinkLabel(''); }}
                  className="text-stone-600 hover:text-stone-900"
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
          
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              
              {/* Image Upload Button */}
              <input
                type="file"
                ref={imageInputRef}
                onChange={handleImageUpload}
                accept="image/jpeg,image/png,image/gif,image/webp"
                className="hidden"
              />
              <button
                type="button"
                onClick={() => imageInputRef.current?.click()}
                disabled={uploading}
                className="flex items-center gap-1.5 text-stone-600 hover:text-[#EFB342] transition-colors text-xs"
                title="Add an image"
              >
                {uploading ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  <Image size={16} />
                )}
                <span>Add Image</span>
              </button>
              
              {/* Add Link Button */}
              <button
                type="button"
                onClick={() => setShowLinkForm(!showLinkForm)}
                className="flex items-center gap-1.5 text-stone-600 hover:text-[#EFB342] transition-colors text-xs"
                title="Add a link"
              >
                <Link size={16} />
                <span>Add Link</span>
              </button>
            </div>
            
            <Button
              onClick={createNote}
              disabled={posting || (!newNote.trim() && !imageUrl)}
              className="bg-[#EFB342] text-black hover:bg-[#D99F30]"
            >
              {posting ? (
                <div className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />
              ) : (
                <>
                  <Send size={16} className="mr-2" />
                  Post Note
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Notes List */}
        <div className="bg-white border border-stone-200 rounded-xl overflow-hidden">
          <div className="p-4 border-b border-stone-200">
            <h2 className="font-semibold text-stone-900">
              {searchTerm ? `Search Results (${sortedNotes.length})` : `All Notes (${sortedNotes.length})`}
            </h2>
          </div>

          {sortedNotes.length === 0 ? (
            <div className="p-8 text-center text-stone-500">
              {searchTerm ? 'No notes match your search' : 'No community notes yet'}
            </div>
          ) : (
            <div className="divide-y divide-zinc-800">
              {sortedNotes.map((note) => {
                const isExpanded = expandedNote === note.id;
                const comments = noteComments[note.id] || [];
                
                return (
                  <div key={note.id} className={`${note.is_pinned ? 'bg-[#EFB342]/5' : ''}`}>
                    <div className="p-4">
                      {/* Note Header */}
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex items-start gap-3 flex-1 min-w-0">
                          <div className="w-10 h-10 bg-[#EFB342]/20 rounded-full flex items-center justify-center flex-shrink-0">
                            <span className="text-[#EFB342] font-bold text-sm">
                              {note.user_name?.charAt(0)?.toUpperCase() || '?'}
                            </span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-medium text-stone-900">{note.user_name}</span>
                              {note.is_pinned && (
                                <span className="flex items-center gap-1 text-[#EFB342] text-xs bg-[#EFB342]/10 px-2 py-0.5 rounded-full">
                                  <Pin size={10} /> Pinned
                                </span>
                              )}
                              <span className="text-stone-500 text-xs flex items-center gap-1">
                                <Clock size={10} />
                                {formatTimeAgo(note.created_at)}
                              </span>
                            </div>
                            <p className="text-stone-700 mt-1 break-words whitespace-pre-wrap">{note.content}</p>
                            
                            {/* Note Image */}
                            {note.image_url && (
                              <div className="mt-2">
                                <img 
                                  src={note.image_url} 
                                  alt="Shared image" 
                                  className="max-w-full rounded-lg border border-stone-300 max-h-64 object-contain cursor-pointer"
                                  onClick={() => window.open(note.image_url, '_blank')}
                                />
                              </div>
                            )}
                            
                            {/* Note Links */}
                            {note.links && note.links.length > 0 && (
                              <div className="mt-2 space-y-2">
                                {note.links.map((link, index) => (
                                  <a
                                    key={index}
                                    href={link.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-2 px-3 py-2 bg-stone-100 hover:bg-stone-200 rounded-lg border border-stone-300 hover:border-[#EFB342]/50 transition-colors group"
                                  >
                                    <Link size={14} className="text-[#EFB342]" />
                                    <span className="text-stone-800 group-hover:text-[#EFB342] transition-colors text-sm">{link.label}</span>
                                    <span className="text-stone-500 text-xs ml-auto truncate max-w-[150px]">{link.url.replace(/^https?:\/\//, '')}</span>
                                  </a>
                                ))}
                              </div>
                            )}
                            
                            {/* Interaction Buttons */}
                            <div className="flex items-center gap-3 mt-3">
                              <div className="relative group">
                                <button
                                  onClick={() => toggleReaction(note.id)}
                                  onMouseEnter={() => note.reactions_count > 0 && fetchReactionNames(note.id)}
                                  className={`flex items-center gap-2 px-3 py-1.5 rounded-full transition-all text-sm ${
                                    note.user_reacted
                                      ? 'bg-[#EFB342]/20 text-[#EFB342]'
                                      : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                                  }`}
                                >
                                  <span role="img" aria-label="fist bump">👊</span>
                                  <span>{note.reactions_count || 0}</span>
                                </button>
                                
                                {/* Tooltip showing who reacted */}
                                {note.reactions_count > 0 && (
                                  <div className="absolute bottom-full left-0 mb-2 px-3 py-2 bg-stone-100 border border-stone-300 rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50 whitespace-nowrap">
                                    {loadingReactions[note.id] ? (
                                      <span className="text-xs text-stone-600">Loading...</span>
                                    ) : reactionNames[note.id]?.length > 0 ? (
                                      <div className="text-xs text-stone-700">
                                        <span className="text-[#EFB342] font-medium">Fist bumps from:</span>
                                        <div className="mt-1">
                                          {reactionNames[note.id].slice(0, 10).join(', ')}
                                          {reactionNames[note.id].length > 10 && ` +${reactionNames[note.id].length - 10} more`}
                                        </div>
                                      </div>
                                    ) : (
                                      <span className="text-xs text-stone-600">Hover to see who reacted</span>
                                    )}
                                    <div className="absolute top-full left-4 border-4 border-transparent border-t-zinc-800"></div>
                                  </div>
                                )}
                              </div>
                              
                              <button
                                onClick={() => toggleExpand(note.id)}
                                className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-stone-100 text-stone-600 hover:bg-stone-200 transition-all text-sm"
                              >
                                <span>💬</span>
                                <span>{note.comments_count || 0}</span>
                                {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                              </button>
                            </div>
                          </div>
                        </div>

                        {/* Admin Actions */}
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => togglePinNote(note.id, note.is_pinned)}
                            disabled={pinningNote === note.id}
                            className={note.is_pinned ? 'text-[#EFB342]' : 'text-stone-600 hover:text-[#EFB342]'}
                            title={note.is_pinned ? 'Unpin' : 'Pin to top'}
                          >
                            {pinningNote === note.id ? (
                              <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                            ) : note.is_pinned ? (
                              <PinOff size={16} />
                            ) : (
                              <Pin size={16} />
                            )}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => deleteNote(note.id)}
                            disabled={deletingNote === note.id}
                            className="text-stone-600 hover:text-red-400"
                            title="Delete note"
                          >
                            {deletingNote === note.id ? (
                              <div className="w-4 h-4 border-2 border-red-400 border-t-transparent rounded-full animate-spin" />
                            ) : (
                              <Trash2 size={16} />
                            )}
                          </Button>
                        </div>
                      </div>

                      {/* Expanded Comments */}
                      {isExpanded && (
                        <div className="mt-4 ml-13 pl-4 border-l-2 border-stone-300 space-y-3">
                          <p className="text-stone-600 text-xs font-medium uppercase">Comments</p>
                          
                          {/* Existing Comments */}
                          {comments.length === 0 && !noteComments[note.id] ? (
                            <p className="text-stone-500 text-sm">Loading comments...</p>
                          ) : comments.length === 0 ? (
                            <p className="text-stone-500 text-sm">No comments yet</p>
                          ) : (
                            comments.map((comment) => (
                              <div key={comment.id} className="flex items-start justify-between gap-3 bg-[#F0EBE1] rounded-lg p-3">
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2">
                                    <span className="font-medium text-stone-900 text-sm">{comment.user_name}</span>
                                    <span className="text-stone-500 text-xs">{formatTimeAgo(comment.created_at)}</span>
                                  </div>
                                  <p className="text-stone-700 text-sm mt-1 break-words">{comment.content}</p>
                                </div>
                                <button
                                  onClick={() => deleteComment(note.id, comment.id)}
                                  className="text-stone-500 hover:text-red-400 p-1"
                                  title="Delete comment"
                                >
                                  <Trash2 size={14} />
                                </button>
                              </div>
                            ))
                          )}
                          
                          {/* Admin Comment Input */}
                          <div className="flex gap-2 mt-3">
                            <Input
                              type="text"
                              value={commentInputs[note.id] || ''}
                              onChange={(e) => setCommentInputs(prev => ({
                                ...prev,
                                [note.id]: e.target.value
                              }))}
                              placeholder="Write a comment as admin..."
                              className="flex-1 bg-stone-100 border-stone-300 text-stone-900 text-sm"
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' && !e.shiftKey) {
                                  e.preventDefault();
                                  addComment(note.id);
                                }
                              }}
                            />
                            <Button
                              onClick={() => addComment(note.id)}
                              disabled={postingComment[note.id] || !commentInputs[note.id]?.trim()}
                              size="sm"
                              className="bg-[#EFB342] text-black hover:bg-[#D99F30]"
                            >
                              {postingComment[note.id] ? (
                                <div className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                              ) : (
                                <Send size={14} />
                              )}
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default AdminCommunity;
